import React from 'react';
import { Link } from 'react-router';
import { useAuthStatus } from '../../../auth/useAuthStatus';
import { useBrand } from '../../../app/config/brand';
import { SAVE_INDICATOR_LABEL, type SaveIndicatorState } from '../lib/save-indicator';

interface WizardBreadcrumbsProps {
  /**
   * SOR-355 / R17 — the save indicator state derived by `EFilingProvider` from
   * the last save outcome and whether the order changed since. It replaced the
   * `saveMessage` string a 3-second timer used to clear, after which a
   * persisted draft read "Not saved"; this badge now stays truthful until the
   * order actually changes.
   */
  saveIndicator: SaveIndicatorState;
}

// Presentation only — the state and its labels come from `save-indicator` (the
// ONE catalog every surface reads). Only the dot color is local: this badge is
// its single consumer.
const SAVE_INDICATOR_DOT_COLOR: Record<SaveIndicatorState, string> = {
  saved: '#0F9D6B',
  failed: '#EF4444',
  unsaved: '#94A3B8',
};

const WizardBreadcrumbs: React.FC<WizardBreadcrumbsProps> = ({ saveIndicator }) => {
  const { isPlatformUser } = useAuthStatus();
  const brand = useBrand();

  return (
    <div
      className="flex items-center justify-between px-6 py-2 text-xs"
      style={{ backgroundColor: brand.colors.bgLight, borderBottom: `1px solid ${brand.colors.border}` }}
    >
      {/* Left: breadcrumb path */}
      <nav className="flex items-center gap-1.5 text-gray-500">
        {isPlatformUser ? (
          <>
            <Link
              to="/admin/dashboard"
              className="hover:text-gray-700 transition-colors"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Admin
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-700 font-medium">E-Filing</span>
          </>
        ) : (
          <>
            <Link
              to="/"
              className="hover:text-gray-700 transition-colors"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Dashboard
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-400">New Order</span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-700 font-medium">eFiling</span>
          </>
        )}
      </nav>

      {/* Right: autosave indicator */}
      <div className="flex items-center gap-1.5" data-testid="wizard-save-indicator">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: SAVE_INDICATOR_DOT_COLOR[saveIndicator] }}
        />
        <span className="text-gray-500">{SAVE_INDICATOR_LABEL[saveIndicator]}</span>
      </div>
    </div>
  );
};

export default WizardBreadcrumbs;
